import React from 'react';

import {BoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {useExperimentsStore} from 'apps/experiments/src/store';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {ExperimentGlobalFilterSettings} from 'iron/apps/experiments/src/types';

import {PageTrackingEvent, trackExperimentPageAction, TrackingEventSource} from '../tracking';
import {useStagedExperiment} from './staged-experiment';

export function useExperimentGlobalFilters() {
  const {stagedExperiment, stageChanges} = useStagedExperiment();
  const setDraftExperiment = useExperimentsStore((state) => state.setDraftExperiment);

  const globalFilters = React.useMemo(
    () => ({
      filters: [],
      ...stagedExperiment?.settings?.globalFilters,
    }),
    [stagedExperiment?.settings?.globalFilters],
  );

  const additionalFilters = React.useMemo(
    () =>
      globalFilters.filters?.map((propertyFilter) =>
        insightsFilterAttrsFromPropertyFilter({dataset: DATASET_MIXPANEL, ...propertyFilter}),
      ),
    [globalFilters.filters],
  );

  const onVariantsFilterChange = React.useCallback(
    (newVariantFilter?: string) => {
      stageChanges({
        settings: {
          globalFilters: {
            ...stagedExperiment?.settings?.globalFilters,
            filters: stagedExperiment?.settings?.globalFilters?.filters ?? [],
            variantsFilter: newVariantFilter,
          } satisfies ExperimentGlobalFilterSettings,
        },
      });
      trackExperimentPageAction({
        event: PageTrackingEvent.ChangeGlobalVariantFilter,
        experiment: stagedExperiment,
        source: TrackingEventSource.ExperimentPageFilterBar,
      });
    },
    [stagedExperiment, stageChanges, setDraftExperiment],
  );

  const onFiltersChange = React.useCallback(
    (newBoardFilters: BoardFilter[]) => {
      stageChanges({
        settings: {
          globalFilters: {
            ...stagedExperiment?.settings?.globalFilters,
            filters: newBoardFilters,
          } satisfies ExperimentGlobalFilterSettings,
        },
      });
      trackExperimentPageAction({
        event: PageTrackingEvent.ChangeGlobalFilter,
        experiment: stagedExperiment,
        source: TrackingEventSource.ExperimentPageFilterBar,
      });
    },
    [stagedExperiment, stageChanges, setDraftExperiment],
  );

  return {
    globalFilters,
    additionalFilters,
    onFiltersChange,
    onVariantsFilterChange,
  };
}
